import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { ContractService } from 'src/app/contract/shared/contract.service';
import { UserService } from 'src/app/shared/user.service';
import { Router, ActivatedRoute } from '@angular/router';
import { Reviewer } from 'src/app/shared/reviewer.model';
import { Contract } from 'src/app/contract/shared/contract.model';
import { ToastrService } from 'ngx-toastr';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { AddTeamDialogComponent } from '../../add-team-dialog/add-team-dialog.component';
import { DeleteDialogComponent } from 'src/app/contract/contract-master/delete-dialog/delete-dialog.component';
import { RejectReasonDialogComponent } from '../reject-reason-dialog/reject-reason-dialog.component';
import { ViewNotesDialogComponent } from '../contract-notes/view-notes-dialog/view-notes-dialog.component';

@Component({
  selector: 'app-contract-reviewer',
  templateUrl: './contract-reviewer.component.html',
  styleUrls: ['./contract-reviewer.component.scss']
})
export class ContractReviewerComponent implements OnInit {
  @Input() contractId;
  @Input() mode;
  @Input() editable;
  @Output() refreshContract = new EventEmitter<void>();
  contract: Contract;
  reviewers: Reviewer[];
  dataLoading: boolean = true;
  isSendForReview: boolean = false;
  isLoading: boolean = false;
  isToggleLoading: { [key: string]: boolean } = {};

  constructor(public contractService: ContractService, public userService: UserService, private router: Router, private route: ActivatedRoute, private toast: ToastrService, public dialog: MatDialog) { }

  async ngOnInit() {
    await this.contractService.refreshContract(parseInt(this.contractId)).then((res: Contract) => {
      this.contract = res;
    });

    this.refreshReviewer();
  }

  openAddReviewer(): void {
    const dialogRef =
      this.dialog.open(AddTeamDialogComponent, {
        enterAnimationDuration: '400ms',
        exitAnimationDuration: '500ms',
        width: '30%',
        disableClose: true,
        data: {
          refresh: () => this.refreshReviewer(),
          contractId: this.contractId,
          mode: 1
        }
      });
  }

  openDeleteConfirmation(id): void {
    const dialogRef =
      this.dialog.open(DeleteDialogComponent, {
        enterAnimationDuration: '400ms',
        exitAnimationDuration: '500ms',
        width: '25%',
        data: {
          deleteFunction: () => this.delete(id, dialogRef)
        }
      });
  }

  openRejectReason(reviewer): void {
    const dialogRef =
      this.dialog.open(RejectReasonDialogComponent, {
        enterAnimationDuration: '400ms',
        exitAnimationDuration: '500ms',
        width: '40%',
        maxWidth: '90vw',
        data: {
          reason: reviewer.reason,
          contractId: this.contractId,
          userId: reviewer.reviewerId || reviewer.userInfo?.userId,
          reviewerId: reviewer.reviewerId
        }
      });
  }

  async delete(id: number, dialogRef: MatDialogRef<DeleteDialogComponent>) {
    try {
      await this.contractService.deleteReviewer(id);
      this.toast.success('The user has been deleted successfully!', '');
      this.refreshReviewer();
      dialogRef.close();
    } catch (error) {
      this.toast.error('Failed to delete the approver!', 'Error');
    }
  }

  async refreshReviewer() {
    this.dataLoading = true;
    this.reviewers = [];
    const contractId = Number(this.contractId);
    try {
      this.contract = await this.contractService.refreshContract(contractId) as Contract;
      this.reviewers = await this.contractService.getAllContractReviewers(contractId) as Reviewer[];
      this.refreshContract.emit();
    } catch (error) {
      this.toast.error('Failed to load reviewers. Please try again later!', '');
    } finally {
      this.dataLoading = false;
    }
  }

  isAnyReviewPending(): Boolean {
    var returnedValue: Boolean = false;
    this.reviewers.forEach((item: Reviewer) => {
      if (item.status == 0)
        returnedValue = true;
    });
    return returnedValue;
  }

  async sentForReview() {
    this.isLoading = true;
    try {
      const lowestSequenceReviewer = this.reviewers.reduce((minReviewer: Reviewer | null, currentReviewer: Reviewer) => {
        if (minReviewer === null || currentReviewer.sequenceNo < minReviewer.sequenceNo) {
          return currentReviewer;
        } else {
          return minReviewer;
        }
      }, null);
      await this.contractService.updateContractStatus(this.contractId, 8, lowestSequenceReviewer.reviewerId, 'Review Submission').then(() => {
        this.toast.success('Contract Sent for Review!', '');
      })
      this.ngOnInit();
      this.refreshContract.emit();
    } catch {
      this.toast.error('Something went wrong, Please try again later!', '')
    }
    this.isLoading = false;
  }

  async toggleReviewerStatus(id) {
    this.isToggleLoading[id] = true;
    await this.contractService.toggleReviewer(id).then(() => {
      this.ngOnInit();
    })
    this.isToggleLoading[id] = false;
  }

  async recall() {
    this.isLoading = true;
    try {
      await this.contractService.updateContractStatus(this.contractId, 1, null, null).then(() => {
        this.toast.success('Contract Recalled Successfully!', '');
      })
      this.ngOnInit();
      this.refreshContract.emit();
    } catch {
      this.toast.error('Something went wrong, Please try again later!', '')
    }
    this.isLoading = false;
  }

  get showBypassReviewerNotice(): boolean {
    // Show bypass notice when contract is approved (statusId === 3) and no reviewers exist
    // Also check that contract data is loaded
    return !this.dataLoading && this.contract && this.contract.statusId === 3 && (!this.reviewers || this.reviewers.length === 0);
  }

  get showReviewerEmptyState(): boolean {
    // Show empty state when no reviewers exist but it's not a bypass scenario
    // Make sure contract is loaded and status is not 3 (Approved)
    return !this.dataLoading && this.contract && this.contract.statusId !== 3 && (!this.reviewers || this.reviewers.length === 0) && !this.showBypassReviewerNotice;
  }

  openViewNotes(reviewer: Reviewer): void {
    const dialogRef = this.dialog.open(ViewNotesDialogComponent, {
      enterAnimationDuration: '400ms',
      exitAnimationDuration: '500ms',
      width: '700px',
      maxWidth: '90vw',
      disableClose: false,
      autoFocus: false,
      panelClass: 'view-notes-dialog',
      data: {
        contractId: this.contractId,
        userId: reviewer.reviewerId || reviewer.userInfo?.userId,
        userInfo: reviewer.userInfo,
        userName: `${reviewer.userInfo?.firstName || ''} ${reviewer.userInfo?.lastName || ''}`.trim()
      }
    });
  }
}
